import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

/**
 * One row of the org-scoped channel-ledger reconciliation list. Mirrors channel_integrations
 * `OrderSummaryListItemResource` (the flat list shape, NOT the per-order detail resource). Decimal
 * amounts arrive as fixed-precision strings (e.g. "229.460000") — coerce with Number() at render.
 */
export interface LedgerOrderSummaryRow {
  subscription_id: number;
  provider: string | null;
  channel_name: string | null;
  channel_order_id: string | null;
  /** Null when the ledger row has not (yet) been resolved to an Astur order — no deep-link target. */
  astur_order_id: number | null;
  astur_order_number: string | null;
  currency: string | null;
  is_multi_currency: boolean;
  /**
   * One of LEDGER_RECONCILIATION_STATUSES. Typed as `string`, not the union, so a status the backend
   * adds before the frontend does still renders (as its raw slug) instead of being narrowed away.
   */
  reconciliation_status: string;
  discrepancy_flag: boolean;
  discrepancy_amount: string | null;
  gross_principal: string | null;
  net_payout: string | null;
  commission: string | null;
  entry_count: number;
  first_occurred_at: string | null;
  last_occurred_at: string | null;
  computed_at: string | null;
}

/** Laravel paginator envelope, mirroring the channel_item_status list `meta` block. */
export interface LedgerListMeta {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number | null;
  to: number | null;
}

/**
 * Aggregate block over the SAME filtered set (ignoring pagination) — the value-summary tiles.
 * Amounts are decimal strings (money-exact roll-up on the backend); the org may span currencies,
 * so these are unit-less totals the UI presents without a single currency symbol.
 */
export interface LedgerListSummary {
  total: number;
  counts_by_status: Record<string, number>;
  sum_discrepancy_amount: string;
  sum_gross_principal: string;
  sum_net_payout: string;
}

export interface LedgerOrderSummariesResponse {
  data: LedgerOrderSummaryRow[];
  meta: LedgerListMeta;
  summary: LedgerListSummary;
}

/**
 * Every `reconciliation_status` the backend writes and the list endpoint accepts — mirrors
 * channel_integrations ChannelLedgerOrderSummary::reconciliationStatuses(). "discrepant" and
 * "negative_net" are the two needs-review verdicts. Order is the filter-menu display order.
 */
export const LEDGER_RECONCILIATION_STATUSES = ['matched', 'discrepant', 'negative_net', 'credit', 'unresolved', 'insufficient_data'] as const;
export type LedgerReconciliationStatus = (typeof LEDGER_RECONCILIATION_STATUSES)[number];

/**
 * Filters + paging accepted by the list endpoint. Every field is optional; only defined values are
 * sent so the backend applies its own defaults (per_page=50, sort=last_occurred_at desc).
 * `reconciliation_status` is the sole reconciliation-verdict filter ("discrepant" = Needs review);
 * `subscription_id` can only narrow within the org's own set.
 */
export interface LedgerOrderSummariesArgs {
  page?: number;
  limit?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  reconciliation_status?: LedgerReconciliationStatus;
  provider?: string;
  channel_name?: string;
  subscription_id?: number;
  /** ISO date (inclusive) — filters on the order's activity window overlap. */
  date_from?: string;
  date_to?: string;
}

/**
 * Org-scoped LIST + AGGREGATE over channel_ledger_order_summaries — backs the global reconciliation
 * page. Hits the channel_integrations endpoint (Path A: ledger data lives in CI MySQL) via the
 * channelApi transport; the organization is taken from the committed scope and injected as the
 * X-Astur-Organization header by the transport, so no per-request header is needed here (unlike the
 * single-subscription per-order ledger endpoints, which bind x-astur-subscription-id).
 *
 * GET /api/astur/integrations/channel/ledger/order-summaries
 */
export async function getLedgerOrderSummaries(
  scope: RequestScope,
  args: LedgerOrderSummariesArgs,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<LedgerOrderSummariesResponse>> {
  try {
    const params: Record<string, unknown> = {};
    if (args.page != null) params.page = args.page;
    if (args.limit != null) params.limit = args.limit;
    if (args.sort) params.sort = args.sort;
    if (args.direction) params.direction = args.direction;
    if (args.reconciliation_status) params.reconciliation_status = args.reconciliation_status;
    if (args.provider) params.provider = args.provider;
    if (args.channel_name) params.channel_name = args.channel_name;
    if (args.subscription_id != null) params.subscription_id = args.subscription_id;
    if (args.date_from) params.date_from = args.date_from;
    if (args.date_to) params.date_to = args.date_to;

    const res = await scopedChannelApi(scope).get<LedgerOrderSummariesResponse>('astur/integrations/channel/ledger/order-summaries', {
      ...options,
      params,
    });
    // The backend returns { data, meta, summary } unwrapped; tolerate a { data: <envelope> } wrapper
    // too so a future serialization change can't silently break the page.
    const body = res.data as unknown as { meta?: unknown; data?: LedgerOrderSummariesResponse };
    const envelope = body && body.meta === undefined && body.data ? body.data : (res.data as LedgerOrderSummariesResponse);
    return { data: envelope, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
